import { ReactNode } from "react";
import { useWebHandoff } from "@/features/auth/lib/useWebHandoff";
import { HandoffConfirm, HandoffLoader, HandoffSignedInBar } from "@/features/auth/ui/HandoffScreens";

interface WebHandoffGateProps {
    children: ReactNode;
}

/**
 * A page opened from the app or the watch carries a one-time login code
 * (?code= next to source=app on any page, or on the old training-basis link;
 * see features/auth/lib/webHandoff.ts). It is redeemed here, once for the
 * whole site, before any page renders: the person may never have signed in in
 * this browser, and a public page must sign them in as well as an account
 * one. The pages wait meanwhile, so RequireAuth inside neither redeems the
 * code a second time nor bounces to /login, and no return path with the code
 * in it is stored. Someone else signed in in this browser → ask before
 * switching. No code → the pages as they are.
 *
 * A code that seated someone in an account here (nobody was signed in, or
 * the person agreed to switch) leaves the bar «Вы вошли как … Не вы? Выйти ×»
 * over every page until it is closed or that account signs out: a link with
 * someone else's code must not seat a visitor in that account unnoticed,
 * whatever page it leads to.
 *
 * The link's who names the person the app is signed in as: the very person
 * signed in here → the code is dropped unspent and the page shows at once.
 * The few ms that check takes show nothing at all (no loader, no page).
 */
export function WebHandoffGate({ children }: WebHandoffGateProps) {
    const handoff = useWebHandoff();
    if (handoff.confirm) {
        return <HandoffConfirm {...handoff.confirm} />;
    }
    if (handoff.deciding) {
        return null;
    }
    if (handoff.pending) {
        return <HandoffLoader />;
    }
    return (
        <>
            {children}
            {handoff.notice && <HandoffSignedInBar {...handoff.notice} />}
        </>
    );
}
